
import { Component, Watch } from "vue-property-decorator";
import { Namespace } from "@/store/types";
import { Action, Getter, Mutation } from "vuex-class";
import ChannelChange from "@/components/channelChange/ChannelChange.vue";
import {
  ChannelChangeAction,
  ChannelChangeGetter,
  ChannelChangeMutation,
  ChannelDirection
} from "@/components/channelChange/types";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import Player from "@/components/player/components/Player.vue";
import OverlayBase from "@/components/overlay/components/OverlayBase.vue";
import {
  OverlayAction,
  OverlayGetter,
  OverlaySection
} from "@/components/overlay/types";
import apiStorageInterface from "@/services/apiStorageInterface";
import { mixins } from "vue-class-component";
import { EpgHandler } from "@/mixins/EpgHandler";
import { PlayerAction, PlayerGetter } from "@/components/player/types";
import {
  SettingsGetters,
  SettingsMutations
} from "@/components/settings/types";
import PinOverlay from "@/components/settings/components/PinOverlay.vue";
import { VodPageAction } from "@/components/VoD/types";
import { Episode, Movie } from "@/services/apiService/api.types";
import { isMovie } from "@/helpers/utils";
import { EventBus } from "@/main";

@Component({
  components: { OverlayBase, Player, ChannelChange, PinOverlay }
})
export default class PlayerBase extends mixins(EpgHandler) {
  @Action(ChannelChangeAction.UpdateChannelNumber, {
    namespace: Namespace.ChannelChange
  })
  updateChannelNumber!: (digit: number) => void;

  @Action(ChannelChangeAction.UpdateChannel, {
    namespace: Namespace.ChannelChange
  })
  updateChannel!: (direction: ChannelDirection) => void;

  @Action(OverlayAction.UpdateOverlayOpen, { namespace: Namespace.Overlay })
  updateOverlayStatus!: (overlaySectionStatus: OverlaySection) => void;

  @Action(OverlayAction.UpdateChannelEpg, {
    namespace: Namespace.Overlay
  })
  updateChannelEpg!: () => Promise<void>;

  @Action(PlayerAction.UpdatePause, { namespace: Namespace.Player })
  updatePause!: (pause: boolean) => void;

  @Action(OverlayAction.UpdateOverlayOpen, {
    namespace: Namespace.Overlay
  })
  closeOverlay!: () => void;

  @Action(VodPageAction.UpdateClosePlayer, {
    namespace: Namespace.Vod
  })
  updateClosePlayer!: (status: boolean) => void;

  @Mutation(ChannelChangeMutation.SetChannelId, {
    namespace: Namespace.ChannelChange
  })
  setChannelId!: (channelId: number) => void;

  @Mutation(SettingsMutations.setEncodedPin, { namespace: Namespace.Settings })
  setEncodedPin!: (pin: string) => void;

  @Mutation(SettingsMutations.setPinOverlay, { namespace: Namespace.Settings })
  setPinOverlay!: (pinStatus: boolean) => void;

  @Getter(ChannelChangeGetter.GetChannelChange, {
    namespace: Namespace.ChannelChange
  })
  showChannelChange!: string;

  @Getter(OverlayGetter.GetOverlayOpen, {
    namespace: Namespace.Overlay
  })
  getOverlayOpen!: OverlaySection;

  @Getter(ChannelChangeGetter.GetSource, { namespace: Namespace.ChannelChange })
  source!: string;

  @Getter(SettingsGetters.getEncodedPin, { namespace: Namespace.Settings })
  getEncodedPin!: string;

  @Getter(SettingsGetters.getPinOverlay, { namespace: Namespace.Settings })
  getPinOverlay!: string;

  @Getter(OverlayGetter.GetActiveVodItem, {
    namespace: Namespace.Overlay
  })
  getActiveVodItem!: Movie | Episode | null;

  @Getter(PlayerGetter.GetPause, { namespace: Namespace.Player })
  getPause!: boolean;

  //checks if any overlaySection is open
  get overlayOpen(): boolean {
    return this.getOverlayOpen !== "";
  }

  pinRequired = false;

  async mounted(): Promise<void> {
    SpatialNavigation.focus("#playerContainer");
    this.updateActiveEpgItem();
    this.setEncodedPin("");
    const channels = await apiStorageInterface.fetchCustomChannelList();
    for (let i = 0; i < channels.length; i++) {
      if (channels[i].videoLive === this.source && channels[i].parentalLock) {
        this.setPinOverlay(true);
        this.pinRequired = true;
      }
    }
    EventBus.$emit("player-mounted");
  }

  playerActions(event: KeyboardEvent): void {
    if (!this.getPinOverlay) {
      switch (keyCodeFinder(event.keyCode)) {
        case KeySet.KEY_NUMERIC_0:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(0);
          break;
        case KeySet.KEY_NUMERIC_1:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(1);
          break;
        case KeySet.KEY_NUMERIC_2:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(2);
          break;
        case KeySet.KEY_NUMERIC_3:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(3);
          break;
        case KeySet.KEY_NUMERIC_4:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(4);
          break;
        case KeySet.KEY_NUMERIC_5:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(5);
          break;
        case KeySet.KEY_NUMERIC_6:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(6);
          break;
        case KeySet.KEY_NUMERIC_7:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(7);
          break;
        case KeySet.KEY_NUMERIC_8:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(8);
          break;
        case KeySet.KEY_NUMERIC_9:
          if (this.$route.path.search("/vod") < 0) this.updateChannelNumber(9);
          break;
        case KeySet.KEY_CHANNEL_PLUS:
          if (this.$route.path.search("/vod") < 0)
            this.updateChannel(ChannelDirection.ChannelDown);
          break;
        case KeySet.KEY_CHANNEL_MINUS:
          if (this.$route.path.search("/vod") < 0)
            this.updateChannel(ChannelDirection.ChannelUp);
          break;
        case KeySet.KEY_LEFT:
          if (!this.overlayOpen && this.$route.path.search("/vod") < 0) {
            this.updateOverlayStatus("ChannelList");
          }
          break;
        case KeySet.KEY_ENTER:
          if (!this.overlayOpen) {
            this.updateOverlayStatus("PlayerControls");
          }
          break;
        case KeySet.KEY_SPACE:
          if (!this.overlayOpen) {
            this.updatePause(!this.getPause);
            this.updateOverlayStatus("PlayerControls");
          }
          break;
        case KeySet.KEY_RIGHT:
          if (
            !this.overlayOpen &&
            (this.getActiveVodItem === null || !isMovie(this.getActiveVodItem))
          ) {
            this.updateOverlayStatus("ContentList");
          }
          break;
        case KeySet.KEY_BACK:
        case KeySet.KEY_ESC:
          if (!this.overlayOpen) {
            this.closeOverlay();
            if (this.$route.path.search("/vod") < 0) {
              this.$router.back();
            }
            return;
          }

          this.closeOverlay();
          break;
      }
    }
  }

  openOverlayOnCLick(): void {
    if (!this.overlayOpen) {
      this.updateOverlayStatus("PlayerControls");
    }

    return;
  }

  @Watch("source")
  async handleChannelChange(newVal: string): Promise<void> {
    const channels = await apiStorageInterface.fetchCustomChannelList();
    for (let i = 0; i < channels.length; i++) {
      if (channels[i].videoLive === newVal) {
        this.setChannelId(channels[i].id);
        await this.updateChannelEpg();
        this.updateActiveEpgItem();
      }
    }
  }

  @Watch("getOffset")
  handleChannelUpdate(): void {
    this.updateActiveEpgItem();
  }

  @Watch("getPinOverlay")
  handlePinConfirm(newVal: boolean): void {
    if (!newVal) {
      SpatialNavigation.focus("#playerContainer");
    }
  }
}
